import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { capitalize, isEmpty } from 'lodash';
import { useParams, useHistory } from 'react-router-dom';
import { TbCurrencyDollarBrunei, TbPercentage } from 'react-icons/tb';

import FormDate from 'partial/form/FormDate';
import FormInput from 'partial/form/FormInput';
import FormToggle from 'partial/form/FormToggle';
import FormSelect from 'partial/form/FormSelect';
import Form, { ErrorLabel } from 'partial/form/Form';
import FormInputNumber from 'partial/form/FormInputNumber';
import { useMerchantSelected } from 'core-modules/merchants/hooks';
import Button from 'partial/components/Button';

import { initialValues, VALIDATIONS } from '../constants';
import {
  useAddCashback,
  useSelectedCashback,
  useUpdateCashback,
} from '../hooks';

const validationSchema = yup.object({
  title: yup.string().required('Required'),
  amount: yup.string().required('Required'),
  minimum_range_amount: yup
    .string()
    .required('Required')
    .when(VALIDATIONS.AMOUNT_SHOULD_NOT_BE_GREATER),
  maximum_range_amount: yup.string().required('Required'),
  validity_end_date: yup.string().when(VALIDATIONS.DATE_SHOULD_NOT_BE_PAST),
});

function AddEditCashback() {
  const history = useHistory();
  const { type, cashback_id } = useParams();
  const [, data] = useMerchantSelected();
  const [, cashback] = useSelectedCashback(data?.id, cashback_id);
  const [addCashback, isAdding] = useAddCashback();
  const [updateCashback, isUpdating] = useUpdateCashback();
  const [validateOnChange, setValidateOnChange] = useState(false);
  const { handleSubmit, errors, setFieldValue, values, resetForm } = useFormik({
    validationSchema,
    validateOnChange,
    initialValues: {
      title: cashback?.title,
      amount: cashback?.amount,
      status: cashback?.status === 'active',
      amount_type: cashback?.amount_type ?? 'BND',
      minimum_range_amount: cashback?.minimum_range_amount,
      maximum_range_amount: cashback?.maximum_range_amount,
      validity_start_date: cashback?.validity_start_date ?? '',
      validity_end_date: cashback?.validity_end_date ?? '',
    },
    onSubmit: (form) => {
      if (type === 'add')
        return addCashback(form, data?.id, () =>
          history.push('/billers/profile/cashback')
        );
      return updateCashback(form, data?.id, cashback_id, () =>
        history.push('/billers/profile/cashback')
      );
    },
  });

  useEffect(() => {
    if (isEmpty(cashback_id))
      resetForm({
        values: initialValues,
      });
  }, [resetForm, cashback_id]);

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-medium">{`${capitalize(
        type
      )} Cashback Details`}</h1>
      <Form onSubmit={handleSubmit} error={errors}>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <FormInput
              name="title"
              label="Cashback Name"
              onSetFieldValue={setFieldValue}
              value={values?.title ?? ''}
            />
            <ErrorLabel name="title" />
          </div>
          <FormToggle
            name="status"
            label="Status"
            secondary
            onLabel="Active"
            offLabel="Inactive"
            onSetFieldValue={setFieldValue}
            checked={!!values?.status}
          />
          <div />
          <div>
            <FormInputNumber
              name="amount"
              label="Cashback"
              onSetFieldValue={setFieldValue}
              value={values?.amount}
              icon={
                values?.amount_type === 'BND'
                  ? TbCurrencyDollarBrunei
                  : TbPercentage
              }
            />
            <ErrorLabel name="amount" />
          </div>
          <div className="w-44">
            <FormSelect
              name="amount_type"
              label="Type"
              onSetFieldValue={setFieldValue}
              value={values?.amount_type ?? ''}
              placeholder=""
              options={[
                {
                  label: 'BND',
                  value: 'BND',
                },
                {
                  label: 'Percentage',
                  value: 'PERCENTAGE',
                },
              ]}
            />
          </div>
          <div />
          <div className="col-span-3 font-semibold text-gray-500 text-lg">
            Price Amount Range
          </div>
          <div>
            <FormInputNumber
              name="minimum_range_amount"
              label="Minimum Range Amount"
              onSetFieldValue={setFieldValue}
              value={values?.minimum_range_amount}
              icon={TbCurrencyDollarBrunei}
            />
            <ErrorLabel name="minimum_range_amount" />
          </div>
          <div>
            <FormInputNumber
              name="maximum_range_amount"
              label="Maximum Range Amount"
              onSetFieldValue={setFieldValue}
              value={values?.maximum_range_amount}
              icon={TbCurrencyDollarBrunei}
            />
            <ErrorLabel name="maximum_range_amount" />
          </div>
          <div />
          <div className="col-span-3 font-semibold text-gray-500 text-lg">
            Validity (Optional)
          </div>
          <FormDate
            name="validity_start_date"
            label="Start Date"
            minDate={Date.now()}
            onSetFieldValue={setFieldValue}
            value={values?.validity_start_date}
          />
          <div>
            <FormDate
              name="validity_end_date"
              label="End Date"
              minDate={Date.now()}
              onSetFieldValue={setFieldValue}
              value={values?.validity_end_date}
            />
            <ErrorLabel name="validity_end_date" />
          </div>
        </div>
        <div className="flex items-center space-x-2 mt-4">
          <Button
            type="button"
            outlinePrimary
            isLoading={isAdding || isUpdating}
            onClick={() => history.push('/billers/profile/cashback')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            primary
            isLoading={isAdding || isUpdating}
            onClick={() => setValidateOnChange(true)}
          >
            Confirm
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default AddEditCashback;
